import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getApiData, postFormData } from '../../utils/APIHelper';
import { Setting } from '../../utils/Setting';

export const useDocumentsAPI = (userData) => {
  const [loading, setLoading] = useState(false);
  const [isCalled, setIsCalled] = useState(false);

  const getDocumentsList = useCallback(async () => {
    setLoading(true);
    try {
      const contractorId = userData?.id;
      if (!contractorId) {
        toast.error("Contractor ID is missing");
        setLoading(false);
        return [];
      }

      const response = await getApiData(
        `api/user/contractor/${contractorId}/kyb-documents`,
        "GET",
        {}
      );
      
      if (response?.success) {
        setLoading(false);
        return response?.documentLists || [];
      }
      
      setLoading(false);
      return [];
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error("Failed to fetch documents");
      setLoading(false);
      return [];
    }
  }, [userData?.id]);

  const uploadDocument = useCallback(async (file, docId) => {
    if (!file) {
      toast.error("No file selected");
      return false;
    }

    const contractorId = userData?.id;
    if (!contractorId) {
      toast.error("Contractor ID is missing");
      return false;
    }

    if (!docId) {
      toast.error("Document ID is missing");
      return false;
    }

    const formData = new FormData();
    formData.append("id", docId);
    formData.append("client_document", file);

    // Add additional validation data that might be needed
    if (userData?.contractor_data?.id) {
      formData.append("contractor_id", userData.contractor_data.id);
    }

    try {
      console.log('Uploading document with data:', {
        docId: docId,
        contractorId: contractorId,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });

      const response = await postFormData(
        Setting.endpoints.uploadDocument,
        formData
      );
      
      console.log('Upload response:', response);
      
      if (response.success) {
        toast.success("File uploaded successfully");
        setIsCalled(true);
        return true;
      } else {
        console.error('Upload failed:', response);
        toast.error(response.message || "File upload failed");
        return false;
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || "An error occurred during file upload");
      return false;
    }
  }, [userData]);

  const deleteDocument = useCallback(async (id) => {
    const contractorId = userData?.id;
    if (!contractorId) {
      toast.error("Contractor ID is missing");
      return false;
    }

    const body = { id };
    
    try {
      const response = await getApiData(
        Setting.endpoints.deleteDocument,
        "POST",
        body
      );
      
      if (response.status) {
        toast.success(response?.message);
        setIsCalled(true);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Delete error:', error);
      toast.error("An error occurred during file deletion");
      return false;
    }
  }, [userData?.id]);

  const resetIsCalled = useCallback(() => {
    setIsCalled(false);
  }, []);

  return {
    loading,
    isCalled,
    getDocumentsList,
    uploadDocument,
    deleteDocument,
    resetIsCalled
  };
};
